import type { ImplementedLocale, MatchCard, TeamProfileData } from './types'

const copy = {
  'en': {
    eyebrow: 'BallPeek football',
    versus: 'vs',
    breadcrumbLabel: 'Breadcrumb',
    back: 'Back',
    teamsPlayers: 'Teams & Players',
    directoryTitle: 'Teams & Players',
    directoryLead: 'Search teams and players observed within BallPeek’s supported football scope.',
    searchPrompt: 'Enter at least 2 characters to search the supported football scope.',
    searchLabel: 'Search by name',
    searchPlaceholder: 'Enter at least 2 characters',
    searchAction: 'Search',
    all: 'All',
    teams: 'Teams',
    players: 'Players',
    team: 'Team',
    player: 'Player',
    supportedScope: 'Results are limited to supported competitions and observed relationships.',
    loadingDirectory: 'Loading supported teams and players',
    loadingTeam: 'Loading team profile',
    loadingPlayer: 'Loading player profile',
    updating: 'Updating',
    emptyTitle: 'No supported results',
    emptyBody: 'BallPeek only lists teams and players observed in supported competitions. Try another name or type.',
    clearSearch: 'Clear search',
    openTeam: 'Open team',
    openPlayer: 'Open player',
    openMatch: 'Open match',
    page: 'Page',
    previous: 'Previous page',
    next: 'Next page',
    teamNotFound: 'Team not found',
    playerNotFound: 'Player not found',
    notFoundBody: 'This entity is unavailable or outside the supported football scope.',
    searchEntities: 'Search teams and players',
    networkTitle: 'Football data is unavailable',
    networkBody: 'The public read service could not be reached. Existing coverage was not replaced.',
    retry: 'Try again',
    refreshFailed: 'Refresh failed. Showing cached data.',
    previousPlayers: 'Previous players',
    nextPlayers: 'Next players',
    previousAppearances: 'Previous appearances',
    nextAppearances: 'Next appearances',
    evidenceDate: 'Evidence date',
    applyDate: 'Apply date',
    partialCoverage: 'Partial coverage',
    unknownCoverage: 'Coverage is not yet known',
    coverageRange: 'Evidence coverage',
    projectedAt: 'Projected',
    stale: 'Football facts may be out of date. Last-good facts remain visible.',
    source: 'Source and coverage',
    currentMatch: 'Next or live match',
    openCurrentMatch: 'Open current match',
    recentMatches: 'Recent matches',
    noRecentMatches: 'No covered recent matches.',
    descriptiveForm: 'Descriptive form',
    formUnavailable: 'Descriptive form is not covered for this supported scope.',
    playerEvidence: 'Player evidence',
    noPlayerEvidence: 'No supported player evidence on this page.',
    history: 'View all historical matches',
    compare: 'Compare with another team',
    appearances: 'Fixture appearances',
    relationships: 'Supported team relationships',
    statistics: 'Historical statistics',
    statisticsUnavailable: 'Historical statistics are not covered for this player.',
    noStatistics: 'No historical statistics at this evidence date.',
    appearance: 'Appearance record',
    observation: 'Observation',
    confirmedMembership: 'Confirmed relationship',
    startUnknown: 'Membership start date is unknown.',
    endUnknown: 'Membership end date is unknown.',
    bothUnknown: 'Membership start and end dates are unknown.',
    asOf: 'Evidence as of',
    openAppearance: 'Open match',
    openRelationshipTeam: 'Open team',
    noAppearances: 'No supported appearances at this evidence date.',
    noRelationships: 'No supported relationships at this evidence date.',
    sample: 'sample',
    truncated: 'Showing a bounded selection.',
  },
  'zh-CN': {
    eyebrow: 'BallPeek 足球',
    versus: '对阵',
    breadcrumbLabel: '面包屑导航',
    back: '返回',
    teamsPlayers: '球队与球员',
    directoryTitle: '球队与球员',
    directoryLead: '搜索 BallPeek 支持范围内已观测到的球队和球员。',
    searchLabel: '按名称搜索',
    searchPrompt: '至少输入 2 个字符，以搜索受支持的足球范围。',
    searchPlaceholder: '至少输入 2 个字符',
    searchAction: '搜索',
    all: '全部',
    teams: '球队',
    players: '球员',
    team: '球队',
    player: '球员',
    supportedScope: '结果仅包含受支持赛事和有证据支持的关系。',
    loadingDirectory: '正在加载受支持的球队与球员',
    loadingTeam: '正在加载球队资料',
    loadingPlayer: '正在加载球员资料',
    updating: '正在更新',
    emptyTitle: '没有受支持的结果',
    emptyBody: 'BallPeek 仅列出受支持赛事中已观测到的球队和球员。请尝试其他名称或类型。',
    clearSearch: '清除搜索',
    openTeam: '查看球队',
    openPlayer: '查看球员',
    openMatch: '查看比赛',
    page: '页码',
    previous: '上一页',
    next: '下一页',
    teamNotFound: '未找到球队',
    playerNotFound: '未找到球员',
    notFoundBody: '此实体不可用或不在受支持的足球范围内。',
    searchEntities: '搜索球队与球员',
    networkTitle: '足球数据暂不可用',
    networkBody: '无法连接公共读取服务，现有覆盖信息未被替换。',
    retry: '重试',
    refreshFailed: '刷新失败，继续显示缓存数据。',
    previousPlayers: '上一页球员',
    nextPlayers: '下一页球员',
    previousAppearances: '上一页出场记录',
    nextAppearances: '下一页出场记录',
    evidenceDate: '证据日期',
    applyDate: '应用日期',
    partialCoverage: '部分覆盖',
    unknownCoverage: '覆盖范围尚不明确',
    coverageRange: '证据覆盖范围',
    stale: '足球事实可能已过时，仍显示最后一次可用事实。',
    source: '来源与覆盖',
    projectedAt: '投影时间',
    currentMatch: '下一场或进行中的比赛',
    openCurrentMatch: '打开当前比赛',
    recentMatches: '近期比赛',
    descriptiveForm: '描述性状态',
    noRecentMatches: '没有已覆盖的近期比赛。',
    formUnavailable: '此受支持范围暂未覆盖描述性状态。',
    playerEvidence: '球员证据',
    history: '查看全部历史比赛',
    noPlayerEvidence: '此页没有受支持的球员证据。',
    compare: '与另一支球队比较',
    appearances: '比赛出场',
    relationships: '有证据支持的球队关系',
    statistics: '历史统计',
    statisticsUnavailable: '此球员的历史统计暂未覆盖。',
    appearance: '出场记录',
    noStatistics: '此证据日期下没有历史统计。',
    observation: '观测记录',
    confirmedMembership: '已确认关系',
    startUnknown: '关系开始日期未知。',
    endUnknown: '关系结束日期未知。',
    bothUnknown: '关系开始和结束日期均未知。',
    asOf: '证据截止日期',
    openAppearance: '打开比赛',
    openRelationshipTeam: '打开球队',
    noAppearances: '此证据日期下没有受支持的出场记录。',
    noRelationships: '此证据日期下没有受支持的关系。',
    sample: '样本',
    truncated: '当前显示有界结果。',
  },
} as const

export function entityCopy(locale: ImplementedLocale) {
  return copy[locale]
}

const fixtureStateLabels: Record<ImplementedLocale, Record<MatchCard['state'], string>> = {
  'en': {
    scheduled: 'Scheduled',
    kickoff_tbd: 'Kickoff time to be confirmed',
    first_half: 'First half',
    half_time: 'Half-time',
    second_half: 'Second half',
    extra_time: 'Extra time',
    penalties: 'Penalties',
    finished: 'Finished',
    finished_after_extra_time: 'Finished after extra time',
    finished_after_penalties: 'Finished after penalties',
    postponed: 'Postponed',
    cancelled: 'Cancelled',
    abandoned: 'Abandoned',
    interrupted: 'Interrupted',
    suspended: 'Suspended',
    unknown: 'Status unavailable',
  },
  'zh-CN': {
    scheduled: '已安排',
    kickoff_tbd: '开球时间待定',
    first_half: '上半场',
    half_time: '中场休息',
    second_half: '下半场',
    extra_time: '加时赛',
    penalties: '点球大战',
    finished: '已结束',
    finished_after_extra_time: '加时后结束',
    finished_after_penalties: '点球后结束',
    postponed: '已推迟',
    cancelled: '已取消',
    abandoned: '已放弃',
    interrupted: '已中断',
    suspended: '已暂停',
    unknown: '状态不可用',
  },
}

const teamTypeLabels: Record<ImplementedLocale, Record<TeamProfileData['team']['teamType'], string>> = {
  'en': { club: 'Club', national: 'National team', representative: 'Representative team', unknown: 'Team type unavailable' },
  'zh-CN': { club: '俱乐部', national: '国家队', representative: '代表队', unknown: '球队类型不可用' },
}

const appearanceRoleLabels = {
  'en': { starter: 'Starter', substitute: 'Substitute', appeared: 'Appeared' },
  'zh-CN': { starter: '首发', substitute: '替补', appeared: '已出场' },
} as const

export function fixtureStateCopy(locale: ImplementedLocale, state: MatchCard['state']): string {
  return fixtureStateLabels[locale][state]
}

export function teamTypeCopy(locale: ImplementedLocale, type: TeamProfileData['team']['teamType']): string {
  return teamTypeLabels[locale][type]
}

export function appearanceRoleCopy(locale: ImplementedLocale, role: 'starter' | 'substitute' | 'appeared'): string {
  return appearanceRoleLabels[locale][role]
}

const relationshipKindLabels = {
  'en': {
    appearance: 'Appearance record',
    observation: 'Observation',
    confirmed_membership: 'Confirmed relationship',
  },
  'zh-CN': {
    appearance: '出场记录',
    observation: '观测记录',
    confirmed_membership: '已确认关系',
  },
} as const

export function relationshipKindCopy(
  locale: ImplementedLocale,
  kind: 'appearance' | 'observation' | 'confirmed_membership',
): string {
  return relationshipKindLabels[locale][kind]
}

const sourceLabels = {
  'en': {
    api_football: 'Football facts source',
    ballpeek_projection: 'BallPeek projection',
    ballpeek_historical_archive: 'BallPeek historical archive',
  },
  'zh-CN': {
    api_football: '足球事实来源',
    ballpeek_projection: 'BallPeek 投影',
    ballpeek_historical_archive: 'BallPeek 历史档案',
  },
} as const

export function sourceCopy(
  locale: ImplementedLocale,
  source: 'api_football' | 'ballpeek_projection' | 'ballpeek_historical_archive',
): string {
  return sourceLabels[locale][source]
}

const sectionReasonLabels = {
  'en': {
    not_covered: 'This section is not covered.',
    not_announced: 'This section has not been announced.',
    not_available: 'This section is not available.',
    out_of_date: 'This section is out of date.',
    temporarily_unavailable: 'This section is temporarily unavailable.',
  },
  'zh-CN': {
    not_covered: '此部分暂未覆盖。',
    not_announced: '此部分尚未公布。',
    not_available: '此部分不可用。',
    out_of_date: '此部分已过时。',
    temporarily_unavailable: '此部分暂时不可用。',
  },
} as const

const coverageCaveatLabels = {
  'en': {
    partial_coverage: 'Partial coverage',
    field_not_covered: 'Some fields are not covered',
    historical_only: 'Historical evidence only',
    membership_unconfirmed: 'Team relationship is observed, not confirmed as Membership',
    source_limited: 'Source limited',
  },
  'zh-CN': {
    partial_coverage: '覆盖不完整',
    field_not_covered: '部分字段未覆盖',
    historical_only: '仅有历史证据',
    membership_unconfirmed: '球队关系仅为观测，未确认为成员关系',
    source_limited: '来源有限',
  },
} as const

export function sectionReasonCopy(
  locale: ImplementedLocale,
  reason: 'not_covered' | 'not_announced' | 'not_available' | 'out_of_date' | 'temporarily_unavailable',
): string {
  return sectionReasonLabels[locale][reason]
}

export function coverageCaveatCopy(
  locale: ImplementedLocale,
  caveat: 'partial_coverage' | 'field_not_covered' | 'historical_only' | 'membership_unconfirmed' | 'source_limited',
): string {
  return coverageCaveatLabels[locale][caveat]
}

const sectionFreshnessLabels = {
  'en': {
    fresh: 'No current section data is available.',
    stale: 'Section facts are out of date.',
    unavailable: 'No source facts are available.',
    provider_failed: 'Source refresh failed.',
  },
  'zh-CN': {
    fresh: '当前没有可用的部分数据。',
    stale: '此部分事实已过时。',
    unavailable: '没有可用的来源事实。',
    provider_failed: '来源刷新失败。',
  },
} as const

export function sectionFreshnessCopy(
  locale: ImplementedLocale,
  freshness: 'fresh' | 'stale' | 'unavailable' | 'provider_failed',
): string {
  return sectionFreshnessLabels[locale][freshness]
}
